import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { JSONTree } from 'react-json-tree';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from '@amzn/awsui-components-react-v2';
import {
    Table,
    Pagination,
    CollectionPreferences,
    TableProps,
} from '@amzn/awsui-components-react-v3';

import {
    AJAX_CALL_ERROR,
    LOADING_TEXT,
    AUDIT_EMPTY,
    AUDIT_PAGE_SIZE,
} from '../../constants/grimsby';
import { decodeDateNumber } from '../../utils/formatDate';
import getPhoneToolUrl from '../../utils/getPhoneToolUrl';

import {
    selectItems,
    selectNextToken,
    selectError,
    selectIsLoading,
    selectIsLoaded,
    getAuditTrail,
    setIsLoaded,
    resetItems,
} from '../../store/slices/auditTrailSlice';
import { AuditItemData } from '../../interfaces/auditItem';

const PAGE_SIZE_KEY = 'grimsby-audit-page-size';
const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];
const DEFAULT_PAGE_SIZE = 25;

function getStoredPageSize(): number {
    try {
        const stored = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '', 10);
        if (PAGE_SIZE_OPTIONS.includes(stored)) return stored;
    } catch {
        // localStorage unavailable
    }
    return DEFAULT_PAGE_SIZE;
}

function setStoredPageSize(size: number): void {
    try {
        localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
        // localStorage unavailable
    }
}

const auditJsonTheme = {
    scheme: 'auditTheme',
    base00: 'transparent',
    base01: '#3C444F',
    base02: '#4F5A65',
    base03: '#BEBEBE',
    base04: '#b0b0b0',
    base05: '#d0d0d0',
    base06: '#FFFFFF',
    base07: '#f5f5f5',
    base08: '#A1C659',
    base09: '#6FB3D2',
    base0A: '#ddb26f',
    base0B: '#fd971f',
    base0C: '#12cfc0',
    base0D: '#fafafa',
    base0E: '#D381C3',
    base0F: '#deaf8f',
};

const columnDefinitions: Array<TableProps.ColumnDefinition<AuditItemData>> = [
    {
        id: 'modified_by',
        header: 'Modified By',
        cell: (item: AuditItemData) => (
            <Link
                to={{
                    pathname: getPhoneToolUrl(item.resource_modified_by),
                }}
                target="_blank"
            >
                {item.resource_modified_by}
            </Link>
        ),
    },
    {
        id: 'timestamp',
        header: 'Timestamp',
        cell: (item: AuditItemData) =>
            new Date(decodeDateNumber(item.resource_modified_timestamp))
                .toISOString()
                .replace('T', ' ')
                .replace(/\.\d+Z$/, ' UTC'),
    },
    {
        id: 'new_value',
        header: 'New Value',
        cell: (item: AuditItemData) => (
            <JSONTree
                data={item.new_value}
                theme={auditJsonTheme}
                shouldExpandNode={() => false}
            />
        ),
    },
    {
        id: 'old_value',
        header: 'Old Value',
        cell: (item: AuditItemData) => (
            <JSONTree
                data={item.old_value}
                theme={auditJsonTheme}
                shouldExpandNode={() => false}
            />
        ),
    },
];

const AuditTab = ({ id }: { id: string }) => {
    const error = useSelector(selectError);
    const isLoading = useSelector(selectIsLoading);
    const items = useSelector(selectItems);
    const nextToken = useSelector(selectNextToken);
    const isLoaded = useSelector(selectIsLoaded);
    const dispatch = useDispatch();

    const [pageSize, setPageSize] = useState<number>(getStoredPageSize);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
        dispatch(resetItems());
        dispatch(getAuditTrail(id, pageSize));
        return () => {
            dispatch(setIsLoaded(false));
            dispatch(resetItems());
        };
    }, [id, dispatch, pageSize]);

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setStoredPageSize(size);
        setCurrentPage(1);
    };

    if (error) {
        return (
            <Alert data-testid="AuditTabError" header="Error" type="error">
                {AJAX_CALL_ERROR}
            </Alert>
        );
    } else if (isLoaded) {
        const pagesCount = Math.ceil(items.length / pageSize);
        const paginatedItems = items.slice(
            (currentPage - 1) * pageSize,
            currentPage * pageSize,
        );

        return (
            <div data-testid="AuditTabDetails" className="audit-table">
                <Table
                    columnDefinitions={columnDefinitions}
                    loadingText={LOADING_TEXT}
                    loading={isLoading}
                    items={paginatedItems}
                    empty={
                        <div className="awsui-util-t-c">
                            <div className="awsui-util-pt-s awsui-util-mb-xs">
                                <b>{AUDIT_EMPTY}</b>
                            </div>
                        </div>
                    }
                    pagination={
                        <Pagination
                            currentPageIndex={currentPage}
                            pagesCount={pagesCount}
                            openEnd={nextToken !== null}
                            onChange={({ detail }) => {
                                const requestedPage = detail.currentPageIndex;
                                const maxLoadedPage = Math.ceil(
                                    items.length / pageSize,
                                );
                                if (
                                    requestedPage > maxLoadedPage &&
                                    nextToken !== null
                                ) {
                                    dispatch(getAuditTrail(id, pageSize));
                                }
                                setCurrentPage(requestedPage);
                            }}
                        />
                    }
                    preferences={
                        <CollectionPreferences
                            title="Preferences"
                            confirmLabel="Confirm"
                            cancelLabel="Cancel"
                            preferences={{ pageSize }}
                            pageSizePreference={{
                                title: 'Page size',
                                options: PAGE_SIZE_OPTIONS.map((size) => ({
                                    value: size,
                                    label: `${size} items`,
                                })),
                            }}
                            onConfirm={({ detail }) => {
                                if (detail.pageSize) {
                                    handlePageSizeChange(detail.pageSize);
                                }
                            }}
                        />
                    }
                />
            </div>
        );
    } else {
        return <Spinner data-testid="AuditTabLoadingSpinner" />;
    }
};

export default AuditTab;
